import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { MapPin, CheckCircle2, Clock, BadgeCheck, ShieldCheck } from "lucide-react";
import { StatCard } from "./StatCard";
import { OrgStarterChecklist } from "./OrgStarterChecklist";
import { OrgFeedbackSection } from "./OrgFeedbackSection";
import { OrgImpactStrip } from "./OrgImpactStrip";
import { OrgCsvBridge } from "./OrgCsvBridge";
import { PendingHoursList } from "./PendingHoursList";
import { ShowMore } from "./ShowMore";
import { formatEventDate } from "@/lib/dates";

const SORT_OPTIONS = [{ v: "recent", l: "Most recent" }, { v: "applications", l: "Most applications" }];
const INITIAL_OPPS = 6;

function VerificationBanner({ user, verifyOpen, setVerifyOpen, verifyForm, setVerifyForm, submitVerification }) {
  if (user.verified) {
    return (
      <div data-testid="org-verified-banner" className="mt-8 flex items-center gap-2 bg-forest/5 border border-forest/15 rounded-xl px-5 py-3">
        <BadgeCheck className="w-5 h-5 text-forest" />
        <span className="text-sm text-forest font-medium">Your organisation is verified.</span>
        <Link to={`/o/${user.user_id}`} className="text-sm text-forest underline ml-auto" data-testid="view-public-org">View public page</Link>
      </div>
    );
  }
  if (user.verification_status === "pending") {
    return (
      <div data-testid="org-verify-pending" className="mt-8 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
        <ShieldCheck className="w-5 h-5 text-amber-700" />
        <span className="text-sm text-amber-800 font-medium">Verification under review — we'll notify you once approved.</span>
      </div>
    );
  }
  return (
    <div className="mt-8 flex items-center gap-3 bg-white border border-neutral-200 rounded-xl px-5 py-3 flex-wrap">
      <ShieldCheck className="w-5 h-5 text-forest" />
      <span className="text-sm text-neutral-700">Get a verified badge to build trust with volunteers.</span>
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogTrigger asChild>
          <Button data-testid="request-verification-btn" variant="outline" className="rounded-full ml-auto h-9 px-4 text-sm">Get verified</Button>
        </DialogTrigger>
        <DialogContent data-testid="verification-dialog">
          <DialogHeader>
            <DialogTitle>Request verification</DialogTitle>
            <DialogDescription>Share a few details. Our team reviews requests manually.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="verify-website" placeholder="Website (https://...)" value={verifyForm.website} onChange={(e) => setVerifyForm(f => ({ ...f, website: e.target.value }))} />
            <Input data-testid="verify-reg" placeholder="Registration / charity number" value={verifyForm.registration_number} onChange={(e) => setVerifyForm(f => ({ ...f, registration_number: e.target.value }))} />
            <Textarea data-testid="verify-note" placeholder="Anything else we should know?" value={verifyForm.note} onChange={(e) => setVerifyForm(f => ({ ...f, note: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button data-testid="submit-verification-btn" onClick={submitVerification} className="rounded-full bg-forest hover:bg-forest-hover">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function OrgHome({
  user, myOpps, apps, pending, approve, reject, bulkApprove,
  verifyOpen, setVerifyOpen, verifyForm, setVerifyForm, submitVerification,
}) {
  const [oppSort, setOppSort] = useState("recent");
  const [oppLimit, setOppLimit] = useState(INITIAL_OPPS);

  const sortedOpps = useMemo(() => {
    const list = [...myOpps];
    if (oppSort === "applications") {
      list.sort((a, b) => (b.applicant_count || 0) - (a.applicant_count || 0) || (b.created_at || "").localeCompare(a.created_at || ""));
    } else {
      list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return list;
  }, [myOpps, oppSort]);

  const pendingOnly = pending.filter(p => p.status === "pending");

  return (
    <>
      <VerificationBanner
        user={user} verifyOpen={verifyOpen} setVerifyOpen={setVerifyOpen}
        verifyForm={verifyForm} setVerifyForm={setVerifyForm} submitVerification={submitVerification}
      />
      <OrgStarterChecklist user={user} myOpps={myOpps} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
        <StatCard label="Open opportunities" value={myOpps.filter(o => o.status === "open").length} icon={MapPin} />
        <StatCard label="Applicants" value={apps.length} icon={CheckCircle2} />
        <StatCard label="Hours to review" value={pending.filter(p => p.status === "pending").length} icon={Clock} />
      </div>
      <OrgImpactStrip />

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h2 className="font-display text-2xl text-forest tracking-tight">Pending hour approvals</h2>
          <PendingHoursList pending={pendingOnly} approve={approve} reject={reject} bulkApprove={bulkApprove} />
        </div>
        <div>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-display text-2xl text-forest tracking-tight">Your opportunities</h2>
            <div className="flex gap-1.5" data-testid="opps-sort">
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.v}
                  data-testid={`opps-sort-${opt.v}`}
                  onClick={() => { setOppSort(opt.v); setOppLimit(INITIAL_OPPS); }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${oppSort === opt.v ? "bg-forest text-white border-forest" : "bg-white border-neutral-200 text-neutral-600 hover:border-forest/40"}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {sortedOpps.length === 0 && <div className="text-neutral-500 text-sm">Post your first opportunity.</div>}
            {sortedOpps.slice(0, oppLimit).map(o => (
              <Link key={o.id} to={`/opportunities/${o.id}`} className="block bg-white border border-neutral-200 rounded-xl p-5 hover:-translate-y-0.5 transition-transform">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="font-medium text-forest">{o.title}</div>
                  <Badge className="bg-forest/10 text-forest border-none">{o.applicant_count || 0} applicant{(o.applicant_count || 0) === 1 ? "" : "s"}</Badge>
                </div>
                <div className="text-xs text-neutral-500 mt-1">{o.location}{o.event_date ? ` · ${formatEventDate(o.event_date)}` : (o.commitment_type === "ongoing" ? " · Ongoing" : "")}</div>
              </Link>
            ))}
          </div>
          <ShowMore
            shown={Math.min(oppLimit, sortedOpps.length)}
            total={sortedOpps.length}
            onMore={() => setOppLimit(l => l + INITIAL_OPPS)}
            testId="opps-load-more"
          />
        </div>
      </div>
      <OrgFeedbackSection />
      <OrgCsvBridge />
    </>
  );
}
